import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { speichereIdentitaet, loescheIdentitaet } from 'modules/identitaeten/features/identitaetenSlice';
import { erzeugePasswort, erzeugeBenutzername } from 'modules/identitaeten/features/generator';

const inputKlasse =
  'w-full rounded-lg border border-line bg-paper px-3 py-2 text-ink focus:border-brand-600 focus:outline-none';

const wuerfelKlasse =
  'shrink-0 rounded-lg border border-line px-3 py-2 text-sm transition-colors hover:bg-ink/5';

/**
 * Modaler Editor fuer eine Identitaet: Dienst, Link, E-Mail, Benutzername,
 * Passwort und Notiz. Benutzername und Passwort lassen sich per Wuerfel-Button
 * zufaellig erzeugen. Speichert verschluesselt ueber den Slice.
 */
export default function IdentitaetForm({ identitaet, onClose }) {
  const dispatch = useDispatch();
  const [dienst, setDienst] = useState(identitaet.dienst || '');
  const [link, setLink] = useState(identitaet.link || '');
  const [email, setEmail] = useState(identitaet.email || '');
  const [benutzername, setBenutzername] = useState(identitaet.benutzername || '');
  const [passwort, setPasswort] = useState(identitaet.passwort || '');
  const [passwortSichtbar, setPasswortSichtbar] = useState(!identitaet.uuid);
  const [notiz, setNotiz] = useState(identitaet.notiz || '');
  const [fehler, setFehler] = useState(null);
  const [laeuft, setLaeuft] = useState(false);

  const istNeu = !identitaet.uuid;

  const absenden = async (e) => {
    e.preventDefault();
    if (!dienst.trim()) {
      setFehler('Bitte einen Dienst bzw. eine Webseite angeben.');
      return;
    }
    setFehler(null);
    setLaeuft(true);
    try {
      await dispatch(
        speichereIdentitaet({
          uuid: identitaet.uuid,
          sortierung: identitaet.sortierung,
          dienst: dienst.trim(),
          link: link.trim(),
          email: email.trim(),
          benutzername: benutzername.trim(),
          passwort,
          notiz: notiz.trim(),
        })
      ).unwrap();
      onClose();
    } catch (ex) {
      setFehler('Speichern fehlgeschlagen. Bitte erneut versuchen.');
      setLaeuft(false);
    }
  };

  const entfernen = async () => {
    if (!window.confirm('Diese Identität wirklich löschen? Die Zugangsdaten sind danach unwiederbringlich weg.')) return;
    setLaeuft(true);
    try {
      await dispatch(loescheIdentitaet(identitaet.uuid)).unwrap();
      onClose();
    } catch (ex) {
      setFehler('Löschen fehlgeschlagen.');
      setLaeuft(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-paper p-6 shadow-xl">
        <h2 className="mb-4 font-serif text-xl font-semibold text-ink">
          {istNeu ? 'Identität anlegen' : 'Identität bearbeiten'}
        </h2>

        <form onSubmit={absenden} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-ink">Dienst / Webseite</label>
            <input
              value={dienst}
              onChange={(e) => setDienst(e.target.value)}
              placeholder="z. B. Forum XY, Newsletter, Shop"
              className={inputKlasse}
              autoFocus
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-ink">Link</label>
            <input
              type="url"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="https://…"
              className={inputKlasse}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-ink">E-Mail-Adresse</label>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Alias-Adresse für diesen Dienst"
              className={inputKlasse}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-ink">Benutzername</label>
            <div className="flex gap-2">
              <input
                value={benutzername}
                onChange={(e) => setBenutzername(e.target.value)}
                placeholder="neutraler Nickname"
                className={inputKlasse}
              />
              <button
                type="button"
                onClick={() => setBenutzername(erzeugeBenutzername())}
                title="Zufälligen Benutzernamen erzeugen"
                className={wuerfelKlasse}
              >
                🎲
              </button>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-ink">Passwort</label>
            <div className="flex gap-2">
              <input
                type={passwortSichtbar ? 'text' : 'password'}
                value={passwort}
                onChange={(e) => setPasswort(e.target.value)}
                className={`${inputKlasse} font-mono`}
              />
              <button
                type="button"
                onClick={() => setPasswortSichtbar(!passwortSichtbar)}
                title={passwortSichtbar ? 'Verbergen' : 'Anzeigen'}
                className={wuerfelKlasse}
              >
                {passwortSichtbar ? '🙈' : '👁️'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setPasswort(erzeugePasswort());
                  setPasswortSichtbar(true);
                }}
                title="Zufälliges Passwort erzeugen (20 Zeichen)"
                className={wuerfelKlasse}
              >
                🎲
              </button>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-ink">Notiz</label>
            <textarea
              value={notiz}
              onChange={(e) => setNotiz(e.target.value)}
              rows={2}
              placeholder="z. B. Sicherheitsfragen, 2FA-Hinweise"
              className={`${inputKlasse} text-sm`}
            />
          </div>

          {fehler && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{fehler}</p>}

          <div className="flex items-center justify-between gap-3 pt-1">
            {!istNeu ? (
              <button
                type="button"
                onClick={entfernen}
                disabled={laeuft}
                className="rounded-lg border border-red-200 px-3 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-50 disabled:opacity-50"
              >
                Löschen
              </button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-line px-3 py-2 text-sm font-medium text-ink-muted transition-colors hover:bg-ink/5"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={laeuft}
                className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-500 disabled:opacity-50"
              >
                {laeuft ? 'Speichere…' : 'Speichern'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
